import {getColorTag, getFormattedDate} from '@/shared/utils/commons'
import {Constants} from '@/shared/constants/commonConstants'
import PropTypes from 'prop-types'

/**
 * Returns a React component that renders a cell element based on the column configuration.
 *
 * @param {Object} column - The column configuration object.
 * @param {Object} column.customElem - The custom element configuration.
 * @param {string} column.name_of_db_column - The name of the database column.
 * @returns {Function} A function that takes a row object and returns a React component.
 *
 * The returned React component varies based on the type of custom element:
 * - If the type is `Constants.Button`, it returns a button element with the value from the database column.
 * - If the type is `Constants.DateTime`, it returns a span element with the formatted date and time.
 * - If the type is `Constants.Date`, it returns a span element with the formatted date.
 * - For any other type, it returns a span element with the data and applies a color tag.
 */
export const getCellDataElement = column => {
  const {customElem, name_of_db_column} = column
  switch (customElem?.type) {
    case Constants.Button: {
      const ButtonCell = ({row: {original} = {}}) => {
        const btnName = original[name_of_db_column] ?? '-'
        return (
          <button className={customElem?.style} title={btnName}>
            {btnName}
          </button>
        )
      }
      ButtonCell.propTypes = {
        row: PropTypes.shape({
          original: PropTypes.object,
        }),
      }
      return ButtonCell
    }
    case Constants.DateTime: {
      const DateTimeCell = ({row: {original} = {}}) => {
        const date = original[name_of_db_column]
        return (
          <span className={customElem?.style} title={date}>
            {date ? getFormattedDate(date, true) : '-'}
          </span>
        )
      }
      DateTimeCell.propTypes = {
        row: PropTypes.shape({
          original: PropTypes.object,
        }),
      }
      return DateTimeCell
    }
    case Constants.Date: {
      const DateCell = ({row: {original} = {}}) => {
        const date = original[name_of_db_column]
        return (
          <span className={customElem?.style} title={date}>
            {date ? getFormattedDate(date) : '-'}
          </span>
        )
      }
      DateCell.propTypes = {
        row: PropTypes.shape({
          original: PropTypes.object,
        }),
      }
      return DateCell
    }
    default: {
      const DefaultCell = ({row: {original} = {}}) => {
        const data = original[name_of_db_column]
        return <span className={`${getColorTag(data)} ${customElem?.style} break-words`}>{data}</span>
      }
      DefaultCell.propTypes = {
        row: PropTypes.shape({
          original: PropTypes.object,
        }),
      }
      return DefaultCell
    }
  }
}

export const getCellDataColumns = columns => {
  let cellDataColumns = []

  columns.forEach(column => {
    let cellData = getCellDataElement(column)
    cellDataColumns.push({...column, cellData})
  })
  return cellDataColumns
}
